// src-lit/components/product/kd-variant-picker.ts
import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { emitEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';
import type { Variant } from './variant.logic';

const DEFAULT_CARD_SELECTOR = '[data-variant-card]';

/**
 * @element kd-variant-picker
 * @description バリアント選択カードのクリックを監視してイベントを発火する。
 *
 * デフォルト: 自要素スコープ内の `[data-variant-card]` を監視（v2）。
 * `card-selector` を指定した場合は document 全体からその selector を監視（v1 互換）。
 *
 * クリック時に以下を行う:
 * - hidden input (`#product-form-{sectionId} input[name="id"]`) を更新
 * - ブラウザ URL を `?variant={id}` に更新
 * - `kd-product-variant-selected` イベントを発火
 * - 全カードの `aria-pressed` 属性を更新（選択カードのみ true）
 */
@customElement('kd-variant-picker')
export class KdVariantPicker extends LitElement {
  createRenderRoot() {
    return this;
  }

  /** セクションID */
  @property({ type: String, attribute: 'section-id' })
  sectionId = '';

  /** 商品URL */
  @property({ type: String, attribute: 'product-url' })
  productUrl = '';

  /**
   * [v1-compat] 外部カードを監視する場合の CSS selector。
   * v1 では `.product-cart__item` カードが kd-variant-picker の外側にあるため、
   * document 全体から selector で検索する必要がある。
   * v2 では未指定（自要素スコープ内の `[data-variant-card]` を監視）。
   * v1 廃止時にこのプロパティごと削除可能。
   */
  @property({ type: String, attribute: 'card-selector' })
  cardSelector = '';

  private variants: Variant[] = [];

  private variantsMap: Map<string, Variant> = new Map();

  private cardElements: HTMLElement[] = [];

  private boundHandleCardClick: ((e: Event) => void) | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.loadVariantData();
    this.initCardListeners();
    this.syncLandingUrl();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cleanupCardListeners();
  }

  /**
   * <script type="application/json"> からバリアントデータを読み込む
   */
  private loadVariantData(): void {
    const script = this.querySelector<HTMLScriptElement>('script[type="application/json"]');
    if (script?.textContent) {
      this.variants = JSON.parse(script.textContent) as Variant[];
      this.variantsMap = new Map(
        this.variants.map((v) => [String(v.id), v]),
      );
    }
  }

  /**
   * カードのクリックリスナーを初期化する。
   * v2: 自要素スコープ内の `[data-variant-card]` を検索（デフォルト）。
   * v1: [v1-compat] `card-selector` 指定時は document 全体から検索。
   */
  private initCardListeners(): void {
    this.boundHandleCardClick = this.handleCardClick.bind(this);
    this.cardElements = this.cardSelector
      // [v1-compat] 外部カード検索分岐。v1 廃止時は else 節のみ残せばよい。
      ? Array.from(document.querySelectorAll<HTMLElement>(this.cardSelector))
      : Array.from(this.querySelectorAll<HTMLElement>(DEFAULT_CARD_SELECTOR));
    this.cardElements.forEach((card) => {
      card.addEventListener('click', this.boundHandleCardClick!);
    });
  }

  private cleanupCardListeners(): void {
    if (!this.boundHandleCardClick) return;
    this.cardElements.forEach((card) => {
      card.removeEventListener('click', this.boundHandleCardClick!);
    });
    this.boundHandleCardClick = null;
    this.cardElements = [];
  }

  /**
   * カードクリックハンドラ
   */
  private handleCardClick(e: Event): void {
    const target = e.currentTarget as HTMLElement;

    // [v1-compat] 参考小売価格リンクのクリックは無視。
    // v1 の `.product-cart__item` カード内に参考小売価格の外部リンクがあり、
    // そこをクリックしてもバリアント選択が走らないようにする既存動作を維持する。
    // v2 カード UI には該当要素がないため、v1 廃止時にこのブロックごと削除可能。
    if (
      e.target instanceof Element
      && e.target.closest('.product-cart__item-price-reference-description a')
    ) {
      return;
    }

    const variantId = target.dataset.variantId;
    if (!variantId) return;

    const variant = this.variantsMap.get(variantId);
    if (!variant) return;

    this.updateSelectedState(variantId);
    this.updateVariantInput(variant);
    this.updateURL(variant);

    emitEvent<VariantSelectedDetail>(this, EVENT_NAMES.PRODUCT.VARIANT_SELECTED, {
      variantId: String(variant.id),
      price: variant.price,
      compareAtPrice: variant.compare_at_price,
      available: variant.available,
      featuredMedia: variant.featured_media ?? null,
    });
  }

  /**
   * カード要素の選択状態を aria-pressed 属性で表現する
   */
  private updateSelectedState(selectedVariantId: string): void {
    this.cardElements.forEach((card) => {
      const isSelected = card.dataset.variantId === selectedVariantId;
      card.setAttribute('aria-pressed', String(isSelected));
    });
  }

  /**
   * hidden input[name="id"] にバリアントIDをセットする。
   * `#product-form-installment` は [v1-compat] 分割払いフォーム（v2 未対応）。
   * v1 廃止時は `#product-form-installment` セレクタのみ削除可能。
   */
  private updateVariantInput(variant: Variant): void {
    const forms = document.querySelectorAll<HTMLFormElement>(
      `#product-form-${this.sectionId}, #product-form-installment`,
    );
    forms.forEach((form) => {
      const input = form.querySelector<HTMLInputElement>('input[name="id"]');
      if (input) {
        input.value = String(variant.id);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * ブラウザURLを更新する
   */
  private updateURL(variant: Variant): void {
    window.history.replaceState(
      {},
      '',
      `${this.productUrl}?variant=${variant.id}`,
    );
  }

  /**
   * ランディング時に URL `?variant=X` が SSR で確定した Y と異なるとき
   * `?variant=Y` に書き換える (issue #3449)。
   *
   * SSR は v2-selectedVariantId で per-set rep (Y) に揃えているため、
   * URL X だけが浮く状態を解消する。X == Y / クエリなし / Y が読めない場合は何もしない。
   *
   * 注: VARIANT_SELECTED は意図的に emit しない。SSR が Y で全 listener と同期した状態を
   * 作っているため init emit は不要（issue #3449 D4）。URL X の featured_media を期待した
   * ランディングであっても、表示は Y に揃えるという方針。
   */
  private syncLandingUrl(): void {
    const selectedCard = this.cardElements.find((card) => card.getAttribute('aria-pressed') === 'true');
    const y = selectedCard?.dataset.variantId;
    if (!y) return;

    const x = new URLSearchParams(window.location.search).get('variant');
    if (!x || x === y) return;

    const variant = this.variantsMap.get(y);
    if (!variant) return;

    this.updateURL(variant);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-variant-picker': KdVariantPicker;
  }
}
